import type { TariffListing } from "./types";

/**
 * A list of price versions, read as a list of prices. pay-ui spec §6.1.
 *
 * `GET /tariffs` serves every version ever minted, and it has to: each one is what
 * some order was quoted at, and a station may still be pointing at one. What an
 * operator is looking at, though, is a handful of *prices* — "Public USD" with three
 * past figures behind it, not four rows called "Public USD".
 *
 * Folding them is a function here rather than a `reduce` inside the page for the usual
 * reason and one specific one: "the current version" is a judgement — the **active**
 * one, not the newest — and that distinction is invisible until the day a price is
 * retired without a successor, at which point a page that guessed newest would offer
 * an unsellable figure as the price in force.
 */

export interface PriceLineage {
  /** The price itself: stable across every version of it. */
  readonly lineageId: string;
  /** The version in force — what a driver would be quoted now. */
  readonly current: TariffListing;
  /** Every superseded version, newest first. Empty for a price never changed. */
  readonly history: readonly TariffListing[];
  /** Stations on this price, counting those left on an older version. */
  readonly subscriberCount: number;
}

export function groupPrices(versions: readonly TariffListing[]): readonly PriceLineage[] {
  const byLineage = new Map<string, TariffListing[]>();
  for (const version of versions) {
    // Built by copy rather than by pushing into a shared array: the caller's list is a
    // query cache's, and sorting it in place would reorder what another screen renders.
    byLineage.set(version.lineageId, [...(byLineage.get(version.lineageId) ?? []), version]);
  }

  const prices: PriceLineage[] = [];
  for (const [lineageId, rows] of byLineage) {
    const newestFirst = [...rows].sort(byNewest);
    /**
     * The active version, and the newest only as a fallback — a price retired without
     * a successor still has to render, and its last version is what names it.
     *
     * The `undefined` check is not a real case: a group exists because a row is in it.
     * It is what stands in for an assertion.
     */
    const current = newestFirst.find((row) => row.active) ?? newestFirst[0];
    if (current === undefined) continue;

    prices.push({
      lineageId,
      current,
      history: newestFirst.filter((row) => row.id !== current.id),
      // Summed across versions, because a station stranded on a retired version is
      // still selling this price — until the next rotation collects it.
      subscriberCount: rows.reduce((total, row) => total + row.subscriberCount, 0),
    });
  }

  // Ordered by the version in force: a price just created belongs at the top, and the
  // age of a price's oldest version is not what anybody is scanning for.
  return prices.sort((a, b) => byNewest(a.current, b.current));
}

function byNewest(a: TariffListing, b: TariffListing): number {
  // String comparison, deliberately: these are ISO-8601 `Z` timestamps from the
  // server, which sort lexically, and `new Date()` here would be a parse per compare.
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? 1 : -1;

  // Same instant — two versions minted in the same millisecond, which only a test or a
  // seed script produces. The id breaks the tie so the order is at least stable.
  return a.id < b.id ? 1 : -1;
}
